import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography, Input, Button, Switch, Divider, Tag,
  Select, Alert, Spin, Checkbox, Modal, InputNumber, Drawer, message,
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, PlusOutlined, CloseOutlined, UploadOutlined, LinkOutlined, SearchOutlined, ThunderboltOutlined, DeleteOutlined, AppstoreOutlined, RightOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';
import { PrecioAutomatico } from './PrecioAutomatico';

const { Text } = Typography;

const BACKEND = (() => {
  try { return require('../../services/config').config.URL_API.replace('/api', ''); } catch { return ''; }
})();

const resolveImg = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : BACKEND + url;
};

// adj.adjunto puede ser: URL externa (http), path completo (/uploads/...) o solo nombre de archivo
const resolveAdjunto = (adj) => {
  const src = adj?.adjunto ?? adj?.url;
  if (!src) return null;
  if (src.startsWith('http') || src.startsWith('/')) return resolveImg(src);
  return resolveImg(`/uploads/productos/${adj.id_tabla ?? adj.tempId}/${src}`);
};

const buildCatTree = (cats) => {
  const map = {};
  cats.forEach(c => { map[c.id] = { ...c, children: [] }; });
  const roots = [];
  cats.forEach(c => {
    if (c.id_parent && map[c.id_parent]) map[c.id_parent].children.push(map[c.id]);
    else roots.push(map[c.id]);
  });
  const flat = (nodes, depth = 0) => {
    const r = [];
    nodes.sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre)).forEach(n => {
      r.push({ ...n, depth });
      if (n.children?.length) r.push(...flat(n.children, depth + 1));
    });
    return r;
  };
  return flat(roots);
};

const EMPTY = {
  nombre: '', codigo: '', descripcion: '', stock: 0, stock_minimo: 0,
  unidad: '', visible: true, activo: true, destacado: false,
};

// ── Presets de valores ────────────────────────────────────────────────────────
const COLORES_BASICOS = [
  { nombre: 'Amarillo', hex: '#FFD700' }, { nombre: 'Azul', hex: '#2563EB' },
  { nombre: 'Beige', hex: '#F5F5DC' },    { nombre: 'Blanco', hex: '#F8F8F8' },
  { nombre: 'Bordó', hex: '#800020' },    { nombre: 'Celeste', hex: '#87CEEB' },
  { nombre: 'Gris', hex: '#9CA3AF' },     { nombre: 'Negro', hex: '#111827' },
  { nombre: 'Naranja', hex: '#F97316' },  { nombre: 'Rojo', hex: '#DC2626' },
  { nombre: 'Rosa', hex: '#FFC0CB' },     { nombre: 'Verde', hex: '#22C55E' },
  { nombre: 'Violeta', hex: '#7C3AED' },
];
const TALLES_GRUPOS = [
  { grupo: 'Adultos',  valores: ['XS','S','M','L','XL','XXL','XXXL'] },
  { grupo: 'Calzado',  valores: ['35','36','37','38','39','40','41','42','43','44','45'] },
  { grupo: 'Niños',    valores: ['0-3M','3-6M','6-9M','9-12M','1-2A','2-4A','4-6A','6-8A','8-10A','10-12A'] },
];

// ── Modal agregar propiedad ───────────────────────────────────────────────────
// Contenido de la pantalla de edición de una propiedad (reutilizable en el drawer)
const EditPropScreen = ({
  editPropId, propNombre, setPropNombre, propTipo, setPropTipo,
  checkedIds, toggleId, checkedPresets, setCheckedPresets, togglePreset,
  pendingCustom, setPendingCustom, newValNombre, setNewValNombre,
  newValHex, setNewValHex, addCustom, saving, error, handleSave,
  selectedProp, esColor, esTalle,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '16px 24px' }}>
    {editPropId === 'new' && (
      <div>
        <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 6 }}>Nombre de la propiedad</label>
        <Input value={propNombre} onChange={e => setPropNombre(e.target.value)} placeholder="Ej: Material, Tamaño…" autoFocus />
      </div>
    )}

    <div>
      <Text strong style={{ fontSize: 13 }}>Valores</Text>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 10 }}>
        Seleccioná los que aplican a este producto. Si no encontrás uno, agregalo abajo.
      </Text>

      {/* Valores existentes en la propiedad */}
      {selectedProp?.valores?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {selectedProp.valores.map(v => (
              <div key={v.id} onClick={() => toggleId(v.id)} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                border: `1.5px solid ${checkedIds.has(v.id) ? '#2563EB' : '#e5e7eb'}`,
                background: checkedIds.has(v.id) ? '#eff6ff' : '#fff',
                fontSize: 13, userSelect: 'none',
              }}>
                {v.hex && <span style={{ width: 14, height: 14, borderRadius: '50%', background: v.hex, border: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />}
                {v.nombre}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Presets de color */}
      {esColor && (() => {
        const existentes = new Set((selectedProp?.valores || []).map(v => v.nombre));
        const disponibles = COLORES_BASICOS.filter(c => !existentes.has(c.nombre));
        if (!disponibles.length) return null;
        return (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>Colores básicos</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {disponibles.map(c => (
                <div key={c.nombre} onClick={() => togglePreset(c.nombre)} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                  border: `1.5px solid ${checkedPresets.has(c.nombre) ? '#2563EB' : '#e5e7eb'}`,
                  background: checkedPresets.has(c.nombre) ? '#eff6ff' : '#fff',
                  fontSize: 13, userSelect: 'none',
                }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', background: c.hex, border: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }} />
                  {c.nombre}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Presets de talle */}
      {esTalle && (() => {
        const existentes = new Set((selectedProp?.valores || []).map(v => v.nombre));
        return (
          <div style={{ marginBottom: 12 }}>
            {TALLES_GRUPOS.map(g => {
              const disponibles = g.valores.filter(v => !existentes.has(v));
              if (!disponibles.length) return null;
              return (
                <div key={g.grupo} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{g.grupo}</Text>
                    <button style={{ background: 'none', border: 'none', color: '#2563EB', fontSize: 12, cursor: 'pointer', padding: 0 }}
                      onClick={() => setCheckedPresets(prev => { const s = new Set(prev); disponibles.forEach(v => s.add(v)); return s; })}>
                      Seleccionar todos
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {disponibles.map(v => (
                      <div key={v} onClick={() => togglePreset(v)} style={{
                        padding: '3px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, userSelect: 'none',
                        border: `1.5px solid ${checkedPresets.has(v) ? '#2563EB' : '#e5e7eb'}`,
                        background: checkedPresets.has(v) ? '#eff6ff' : '#fff',
                      }}>{v}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Valores personalizados pendientes */}
      {pendingCustom.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {pendingCustom.map((v, i) => (
            <Tag key={i} closable onClose={() => setPendingCustom(prev => prev.filter((_, j) => j !== i))}
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {v.hex && <span style={{ width: 10, height: 10, borderRadius: '50%', background: v.hex, border: '1px solid rgba(0,0,0,0.1)', display: 'inline-block' }} />}
              {v.nombre}
            </Tag>
          ))}
        </div>
      )}

      {/* Agregar valor personalizado */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {esColor && (
          <input type="color" value={newValHex} onChange={e => setNewValHex(e.target.value)}
            style={{ width: 32, height: 32, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 4 }} />
        )}
        <Input size="small" placeholder={esColor ? 'Nombre del color…' : 'Agregar valor…'}
          value={newValNombre} onChange={e => setNewValNombre(e.target.value)} onPressEnter={addCustom} style={{ flex: 1 }} />
        <Button size="small" onClick={addCustom} icon={<PlusOutlined />}>Agregar</Button>
      </div>
    </div>

    {error && <Alert type="error" message={error} showIcon style={{ padding: '4px 12px' }} />}

    <Button type="primary" loading={saving} onClick={handleSave}
      disabled={checkedIds.size === 0 && checkedPresets.size === 0 && pendingCustom.length === 0}>
      Guardar
    </Button>
  </div>
);

const PropiedadesDrawer = ({ open, onClose, propiedadesTienda, productoId, variantesActuales = [], onSaved }) => {
  const [screen, setScreen] = useState('list'); // 'list' | 'edit'
  const [editPropId, setEditPropId] = useState(null);
  const [propNombre, setPropNombre] = useState('');
  const [propTipo, setPropTipo] = useState('custom');
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [checkedPresets, setCheckedPresets] = useState(new Set());
  const [newValNombre, setNewValNombre] = useState('');
  const [newValHex, setNewValHex] = useState('#000000');
  const [pendingCustom, setPendingCustom] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedProp = editPropId && editPropId !== 'new'
    ? propiedadesTienda.find(p => p.id === editPropId)
    : null;
  const esColor = propTipo === 'color' || selectedProp?.tipo === 'color';
  const esTalle = propTipo === 'talle' || selectedProp?.tipo === 'talle';

  const openEdit = (propId) => {
    setEditPropId(propId);
    setPropNombre(''); setPropTipo('custom');
    setCheckedPresets(new Set()); setPendingCustom([]);
    setNewValNombre(''); setNewValHex('#000000'); setError('');
    if (propId && propId !== 'new') {
      const ya = new Set();
      for (const v of variantesActuales)
        for (const val of v.valores)
          if (val.id_propiedad === propId) ya.add(val.id_valor);
      setCheckedIds(ya);
    } else {
      setCheckedIds(new Set());
    }
    setScreen('edit');
  };

  const backToList = () => { setScreen('list'); setEditPropId(null); setError(''); };
  const handleClose = () => { setScreen('list'); setEditPropId(null); setError(''); onClose(); };

  const toggleId = (id) => setCheckedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const togglePreset = (n) => setCheckedPresets(prev => { const s = new Set(prev); s.has(n) ? s.delete(n) : s.add(n); return s; });
  const addCustom = () => {
    const n = newValNombre.trim();
    if (!n) return;
    setPendingCustom(prev => [...prev, { nombre: n, hex: esColor ? newValHex : undefined }]);
    setNewValNombre(''); setNewValHex('#000000');
  };

  const handleSave = async () => {
    setError('');
    const isNew = editPropId === 'new';
    if (isNew && !propNombre.trim()) { setError('Ingresá el nombre de la propiedad'); return; }
    const allSelectedIds = [...checkedIds];
    const allNewValues = [
      ...[...checkedPresets].map(n => {
        const c = esColor ? COLORES_BASICOS.find(c => c.nombre === n) : null;
        return c ? { nombre: n, hex: c.hex } : { nombre: n };
      }),
      ...pendingCustom,
    ];
    if (allSelectedIds.length === 0 && allNewValues.length === 0) { setError('Seleccioná o agregá al menos un valor'); return; }
    if (isNew) {
      const nombreLower = propNombre.trim().toLowerCase();
      const duplicada = propiedadesTienda.find(p => p.nombre.toLowerCase() === nombreLower);
      if (duplicada) {
        setError(`Ya existe una propiedad llamada "${duplicada.nombre}". Editala desde la lista.`);
        return;
      }
    }
    setSaving(true);
    try {
      let propId = isNew ? null : editPropId;
      if (isNew) {
        const res = await api.admin.catalogo.crearPropiedad({ nombre: propNombre.trim(), tipo: 'custom' });
        if (res.status !== 'success') { setError(res.message || 'Error al crear propiedad'); setSaving(false); return; }
        propId = res.data.id;
      }
      const newIds = [];
      for (const v of allNewValues) {
        const res = await api.admin.catalogo.agregarValorPropiedad(propId, v);
        if (res.status === 'success') newIds.push(res.data.id);
      }
      const allValorIds = [...allSelectedIds, ...newIds];
      // Producto cartesiano entre dimensiones
      const propValueMap = {};
      for (const v of variantesActuales)
        for (const val of v.valores) {
          if (!propValueMap[val.id_propiedad]) propValueMap[val.id_propiedad] = new Set();
          propValueMap[val.id_propiedad].add(val.id_valor);
        }
      if (allValorIds.length > 0) propValueMap[propId] = new Set(allValorIds);
      else delete propValueMap[propId];
      const dimensions = Object.values(propValueMap).map(s => [...s]);
      let combos = [[]];
      for (const dim of dimensions) combos = combos.flatMap(combo => dim.map(valId => [...combo, valId]));
      await api.admin.catalogo.crearVariantes(productoId, combos.map(valor_ids => ({ valor_ids, stock: 0 })));
      message.success('Variantes actualizadas');
      onSaved();
      backToList();
    } catch { setError('Error inesperado'); }
    finally { setSaving(false); }
  };

  // Propiedades en uso por este producto (derivado de variantesActuales)
  const propsUsadas = (() => {
    const map = {};
    for (const v of variantesActuales)
      for (const val of v.valores) {
        if (!map[val.id_propiedad]) {
          const def = propiedadesTienda.find(p => p.id === val.id_propiedad);
          map[val.id_propiedad] = { id: val.id_propiedad, nombre: val.nombre_propiedad, tipo: def?.tipo || 'custom', valores: [] };
        }
        if (!map[val.id_propiedad].valores.find(vv => vv.id === val.id_valor))
          map[val.id_propiedad].valores.push({ id: val.id_valor, nombre: val.nombre, hex: val.hex });
      }
    return Object.values(map);
  })();
  const propsDisponibles = propiedadesTienda.filter(p => !propsUsadas.find(u => u.id === p.id));

  const drawerTitle = screen === 'list' ? 'Propiedades'
    : editPropId === 'new' ? 'Nueva propiedad'
    : (selectedProp?.nombre || 'Editar propiedad');

  return (
    <Drawer
      open={open}
      onClose={screen === 'edit' ? backToList : handleClose}
      width={420}
      title={drawerTitle}
      closeIcon={screen === 'edit' ? <ArrowLeftOutlined /> : undefined}
      extra={screen === 'edit' ? <CloseOutlined onClick={handleClose} style={{ cursor: 'pointer', color: '#9ca3af' }} /> : null}
      styles={{ body: { padding: 0 } }}
    >
      {screen === 'list' ? (
        <div>
          {/* Crear nueva */}
          <div style={{ padding: '12px 24px', borderBottom: '1px solid #f3f4f6' }}>
            <Button type="link" icon={<PlusOutlined />} onClick={() => openEdit('new')} style={{ paddingLeft: 0, fontWeight: 500 }}>
              Crear nueva propiedad
            </Button>
          </div>

          {/* Propiedades que ya usa este producto */}
          {propsUsadas.map(p => (
            <div key={p.id} onClick={() => openEdit(p.id)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>{p.nombre}</Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {p.valores.map(v => (
                    <span key={v.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 12, background: '#f3f4f6', fontSize: 12 }}>
                      {v.hex && <span style={{ width: 10, height: 10, borderRadius: '50%', background: v.hex, border: '1px solid rgba(0,0,0,0.1)' }} />}
                      {v.nombre}
                    </span>
                  ))}
                </div>
              </div>
              <RightOutlined style={{ color: '#9ca3af', fontSize: 12, flexShrink: 0 }} />
            </div>
          ))}

          {/* Otras propiedades disponibles de la tienda */}
          {propsDisponibles.length > 0 && (
            <>
              {propsUsadas.length > 0 && (
                <div style={{ padding: '12px 24px 4px' }}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Otras propiedades disponibles
                  </Text>
                </div>
              )}
              {propsDisponibles.map(p => (
                <div key={p.id} onClick={() => openEdit(p.id)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Text style={{ color: '#6b7280' }}>{p.nombre}</Text>
                  <RightOutlined style={{ color: '#d1d5db', fontSize: 12 }} />
                </div>
              ))}
            </>
          )}

          {propsUsadas.length === 0 && propsDisponibles.length === 0 && (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>No hay propiedades. Creá una nueva arriba.</Text>
            </div>
          )}
        </div>
      ) : (
        <EditPropScreen
          editPropId={editPropId} propNombre={propNombre} setPropNombre={setPropNombre}
          propTipo={propTipo} setPropTipo={setPropTipo}
          checkedIds={checkedIds} toggleId={toggleId}
          checkedPresets={checkedPresets} setCheckedPresets={setCheckedPresets} togglePreset={togglePreset}
          pendingCustom={pendingCustom} setPendingCustom={setPendingCustom}
          newValNombre={newValNombre} setNewValNombre={setNewValNombre}
          newValHex={newValHex} setNewValHex={setNewValHex}
          addCustom={addCustom} saving={saving} error={error} handleSave={handleSave}
          selectedProp={selectedProp} esColor={esColor} esTalle={esTalle}
        />
      )}
    </Drawer>
  );
};

// ── Sección de variantes ──────────────────────────────────────────────────────
const VariantesSection = ({ productoId, primary, primaryText, onVariantesChange, tiposPrecio = [], preciosProducto = [] }) => {
  const [variantes, setVariantes] = useState([]);
  const [propiedadesTienda, setPropiedadesTienda] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBuf, setEditBuf] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    const [resVar, resProps] = await Promise.all([
      api.admin.catalogo.getVariantes(productoId),
      api.admin.catalogo.getPropiedades(),
    ]);
    const vars = resVar.variantes || [];
    setVariantes(vars);
    setPropiedadesTienda(resProps.data || []);
    setLoading(false);
    onVariantesChange?.(vars.length > 0);
  }, [productoId, onVariantesChange]);

  useEffect(() => { cargar(); }, [cargar]);

  // Derivar propiedades usadas en las variantes actuales
  const propsEnUso = (() => {
    const map = {};
    for (const v of variantes) {
      for (const val of v.valores) {
        if (!map[val.id_propiedad]) map[val.id_propiedad] = { nombre: val.nombre_propiedad, valores: new Set() };
        map[val.id_propiedad].valores.add(val.nombre);
      }
    }
    return Object.values(map);
  })();

  const labelVariante = (v) => v.valores.map(vv => vv.nombre).join(' / ') || '(sin valores)';

  const startEdit = (v) => {
    setEditingId(v.id);
    // preciosBuf: { [id_tipo_precio]: precio_actual }
    const preciosBuf = {};
    (v.precios || []).forEach(p => { preciosBuf[p.id_tipo_precio] = p.precio; });
    setEditBuf({ sku: v.sku || '', stock: v.stock, activo: v.activo, precios: preciosBuf });
  };

  const saveEdit = async (id) => {
    setSavingId(id);
    // Guardar stock / sku / activo
    await api.admin.catalogo.editarVariante(id, {
      sku: editBuf.sku || null,
      stock: Number(editBuf.stock) || 0,
      activo: editBuf.activo,
    });
    // Guardar precios por tipo — solo si el valor fue modificado (no vacío)
    const preciosSaves = Object.entries(editBuf.precios || {})
      .filter(([, precio]) => precio !== '' && precio != null)
      .map(([tipoId, precio]) =>
        api.admin.catalogo.agregarPrecio(productoId, {
          id_tipo_precio: Number(tipoId),
          precio: Number(precio),
          id_variante: id,
        })
      );
    await Promise.all(preciosSaves);
    setSavingId(null);
    setEditingId(null);
    message.success('Variante guardada');
    cargar();
  };

  const deleteVariante = async (id) => {
    setDeletingId(id);
    await api.admin.catalogo.eliminarVariante(id);
    setDeletingId(null);
    message.success('Variante eliminada');
    cargar();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>;

  return (
    <div>
      <PropiedadesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        propiedadesTienda={propiedadesTienda}
        productoId={productoId}
        variantesActuales={variantes}
        onSaved={cargar}
      />

      {/* Tabla de variantes */}
      {variantes.length > 0 ? (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
          {/* Header */}
          {(() => {
            const cols = `2fr 1.2fr ${tiposPrecio.map(() => '1fr').join(' ')} 1fr 80px 80px`;
            return (
              <div style={{
                display: 'grid', gridTemplateColumns: cols,
                padding: '8px 12px', background: '#f9fafb',
                borderBottom: '1px solid #e5e7eb', fontSize: 12, fontWeight: 600, color: '#6b7280',
              }}>
                <span>Variante</span>
                <span>SKU</span>
                {tiposPrecio.map(t => <span key={t.id}>{t.nombre}</span>)}
                <span>Stock</span><span>Activo</span><span></span>
              </div>
            );
          })()}
          {variantes.map((v, idx) => {
            const isEditing = editingId === v.id;
            const cols = `2fr 1.2fr ${tiposPrecio.map(() => '1fr').join(' ')} 1fr 80px 80px`;
            return (
              <div
                key={v.id}
                style={{
                  display: 'grid', gridTemplateColumns: cols,
                  padding: '8px 12px', alignItems: 'center', gap: 4,
                  borderBottom: idx < variantes.length - 1 ? '1px solid #f3f4f6' : 'none',
                  background: isEditing ? '#f0f9ff' : '#fff',
                }}
              >
                {/* Label */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {v.valores.map(vv => (
                    <span key={vv.id_valor} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {vv.hex && <span style={{ width: 12, height: 12, borderRadius: '50%', background: vv.hex, border: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />}
                      <Text style={{ fontSize: 13, fontWeight: 500 }}>{vv.nombre}</Text>
                    </span>
                  ))}
                  {v.valores.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>Sin valores</Text>}
                </div>

                {/* SKU */}
                {isEditing
                  ? <Input size="small" value={editBuf.sku} onChange={e => setEditBuf(b => ({ ...b, sku: e.target.value }))} placeholder="SKU" />
                  : <Text style={{ fontSize: 12, color: '#6b7280' }}>{v.sku || '—'}</Text>
                }

                {/* Precio por tipo (Minorista, Mayorista, etc.) */}
                {tiposPrecio.map(t => {
                  const precioActual = v.precios?.find(p => p.id_tipo_precio === t.id);
                  const precioPadre = preciosProducto.find(p => p.id_tipo_precio === t.id)?.precio;
                  return isEditing
                    ? (
                      <InputNumber
                        key={t.id}
                        size="small"
                        value={editBuf.precios?.[t.id] ?? ''}
                        onChange={val => setEditBuf(b => ({ ...b, precios: { ...b.precios, [t.id]: val ?? '' } }))}
                        placeholder={precioPadre != null ? String(precioPadre) : '—'}
                        min={0}
                        prefix="$"
                        style={{ width: '100%' }}
                      />
                    )
                    : (
                      <Text key={t.id} style={{ fontSize: 13 }}>
                        {precioActual ? `$${Number(precioActual.precio).toFixed(2)}` : <span style={{ color: '#9ca3af' }}>—</span>}
                      </Text>
                    );
                })}

                {/* Stock */}
                {isEditing
                  ? <InputNumber size="small" value={editBuf.stock} onChange={val => setEditBuf(b => ({ ...b, stock: val ?? 0 }))} min={0} style={{ width: '100%' }} />
                  : <Text style={{ fontSize: 13 }}>{v.stock}</Text>
                }

                {/* Activo */}
                {isEditing
                  ? <Switch size="small" checked={editBuf.activo} onChange={val => setEditBuf(b => ({ ...b, activo: val }))} />
                  : <Switch size="small" checked={v.activo} disabled />
                }

                {/* Acciones */}
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  {isEditing ? (
                    <>
                      <Button size="small" type="primary" loading={savingId === v.id} onClick={() => saveEdit(v.id)}
                        style={{ fontSize: 11, padding: '0 6px' }}>✓</Button>
                      <Button size="small" onClick={() => setEditingId(null)} style={{ fontSize: 11, padding: '0 6px' }}>✕</Button>
                    </>
                  ) : (
                    <>
                      <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(v)} />
                      <Button size="small" danger icon={<DeleteOutlined />}
                        loading={deletingId === v.id}
                        onClick={() => deleteVariante(v.id)} />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{
          border: '2px dashed #e5e7eb', borderRadius: 8, padding: '24px 16px',
          textAlign: 'center', marginBottom: 16,
        }}>
          <AppstoreOutlined style={{ fontSize: 28, color: '#d1d5db', marginBottom: 8, display: 'block' }} />
          <Text type="secondary" style={{ fontSize: 13 }}>
            Este producto no tiene variantes todavía.<br />
            Agregá una propiedad (color, talle, etc.) para generar variantes.
          </Text>
        </div>
      )}

      <Button icon={<AppstoreOutlined />} onClick={() => setDrawerOpen(true)}>
        {variantes.length > 0 ? 'Gestionar propiedades' : 'Agregar variantes'}
      </Button>
    </div>
  );
};

const Section = ({ title, description, children }) => (
  <div style={{
    background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 8, padding: 24, marginBottom: 16,
  }}>
    {title && (
      <Text strong style={{ display: 'block', fontSize: 16, marginBottom: description ? 4 : 20 }}>
        {title}
      </Text>
    )}
    {description && (
      <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 20 }}>
        {description}
      </Text>
    )}
    {children}
  </div>
);

const Field = ({ label, helper, children, style }) => (
  <div style={style}>
    {label && (
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
        {label}
      </label>
    )}
    {children}
    {helper && (
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
        {helper}
      </Text>
    )}
  </div>
);

export const AdminProductoForm = () => {
  const { id } = useParams();
  const isEdit = !!id && id !== 'nuevo';
  const navigate = useNavigate();
  const { clientId, theme , clientPath, tieneCapacidad } = useClient();
  const primary = theme?.primaryColor || '#0f172a';
  const primaryText = theme?.primaryTextColor || '#fff';

  const [form, setForm] = useState(EMPTY);
  const [producto, setProducto] = useState(null);
  const [precios, setPrecios] = useState([]);
  const [tiposPrecio, setTiposPrecio] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [catSeleccionadas, setCatSeleccionadas] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Asociados
  const [asociados, setAsociados] = useState([]);
  const [asociadoQuery, setAsociadoQuery] = useState('');
  const [asociadoResults, setAsociadoResults] = useState([]);
  const [buscandoAsociado, setBuscandoAsociado] = useState(false);

  // Adjuntos (imágenes)
  const [adjuntos, setAdjuntos] = useState([]);           // edit mode: loaded from API
  const [adjBorradores, setAdjBorradores] = useState([]); // new mode: buffered {url}
  const [adjUrl, setAdjUrl] = useState('');
  const [adjUrlError, setAdjUrlError] = useState('');
  const [uploadingAdj, setUploadingAdj] = useState(false);
  const [deletingAdjId, setDeletingAdjId] = useState(null);
  const fileInputRef = useRef(null);

  // SerpAPI image search
  const [serpapiQuery, setSerpapiQuery] = useState('');
  const [serpapiResults, setSerpapiResults] = useState(null); // null = never searched
  const [serpapiLoading, setSerpapiLoading] = useState(false);
  const [serpapiError, setSerpapiError] = useState('');
  const [addingSerpapiUrl, setAddingSerpapiUrl] = useState('');

  // Inline price form
  const [showPrecioForm, setShowPrecioForm] = useState(false);
  const [inlineListaId, setInlineListaId] = useState('');
  const [inlinePrecioVal, setInlinePrecioVal] = useState('');
  const [inlineEditingTipo, setInlineEditingTipo] = useState(null);
  const [savingPrecioInline, setSavingPrecioInline] = useState(false);
  const [precioInlineMsg, setPrecioInlineMsg] = useState({ type: '', text: '' });
  const [preciosBorradores, setPreciosBorradores] = useState([]);
  const [productoTieneVariantes, setProductoTieneVariantes] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [resProducto, resTipos, resCats] = await Promise.all([
        api.admin.catalogo.getProducto(id),
        api.admin.catalogo.getTiposPrecio(),
        api.admin.catalogo.getCategorias(),
      ]);
      if (resProducto.status === 'success') {
        const p = resProducto.data.producto;
        setProducto(p);
        setForm({
          nombre: p.nombre || '',
          codigo: p.codigo || '',
          descripcion: p.descripcion || '',
          stock: p.stock ?? 0,
          stock_minimo: p.stock_minimo ?? 0,
          unidad: p.unidad || '',
          visible: !!p.visible,
          activo: !!p.activo,
          destacado: !!p.destacado,
        });
        setPrecios(resProducto.data.precios || []);
        setCatSeleccionadas(resProducto.data.categorias_ids || []);
        setAdjuntos(resProducto.data.adjuntos || []);
        setAsociados(resProducto.data.asociados || []);
      } else {
        setError(resProducto.message || 'No se pudo cargar el producto');
      }
      if (resTipos.status === 'success') setTiposPrecio(resTipos.data || []);
      if (resCats.status === 'success') setCategorias(resCats.data || []);
    } catch {
      setError('Error de red o servidor');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      cargar();
    } else {
      Promise.all([
        api.admin.catalogo.getTiposPrecio(),
        api.admin.catalogo.getCategorias(),
      ]).then(([resTipos, resCats]) => {
        if (resTipos.status === 'success') setTiposPrecio(resTipos.data || []);
        if (resCats.status === 'success') setCategorias(resCats.data || []);
      });
    }
  }, [isEdit, cargar]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        nombre: form.nombre,
        ...(form.codigo && { codigo: form.codigo }),
        ...(form.descripcion && { descripcion: form.descripcion }),
        stock: Number(form.stock),
        stock_minimo: Number(form.stock_minimo),
        ...(form.unidad && { unidad: form.unidad }),
        visible: form.visible,
        destacado: form.destacado,
      };
      const res = isEdit
        ? await api.admin.catalogo.editarProducto(id, payload)
        : await api.admin.catalogo.crearProducto(payload);

      if (res.status === 'success') {
        const productId = isEdit ? id : res.data.id;
        await Promise.all([
          api.admin.catalogo.setProductoCategorias(productId, catSeleccionadas),
          api.admin.catalogo.setProductoAsociados(productId, asociados.map(a => a.id)),
          ...(!isEdit ? [
            ...preciosBorradores.map(p =>
              api.admin.catalogo.agregarPrecio(productId, { id_tipo_precio: p.id_tipo_precio, precio: p.precio })
            ),
            ...adjBorradores.map(a =>
              api.admin.catalogo.addProductoAdjunto(productId, a.url)
            ),
          ] : []),
        ]);
        if (!isEdit) {
          message.success('Producto creado correctamente');
          navigate(clientPath(`/admin/productos/${res.data.id}`), { replace: true });
        } else {
          message.success('Cambios guardados');
          setProducto(res.data);
        }
      } else {
        message.error(res.message || 'No se pudo guardar');
        setError(res.message || 'No se pudo guardar');
      }
    } catch {
      message.error('Error de red o servidor');
      setError('Error de red o servidor');
    } finally {
      setSaving(false);
    }
  };

  const toggleCat = (catId) => {
    setCatSeleccionadas(prev =>
      prev.includes(catId) ? prev.filter(i => i !== catId) : [...prev, catId]
    );
  };

  // ── Adjuntos handlers ────────────────────────────────────────────────────────

  const handleAddAdjuntoUrl = async () => {
    const url = adjUrl.trim();
    if (!url) return;
    if (!isEdit) {
      setAdjBorradores(prev => [...prev, { url, tempId: Date.now() }]);
      setAdjUrl('');
      return;
    }
    try {
      const res = await api.admin.catalogo.addProductoAdjunto(id, url);
      if (res.status === 'success') {
        setAdjuntos(prev => [...prev, res.data]);
        setAdjUrl('');
        message.success('Imagen agregada');
      } else {
        message.error(res.message || 'Error al guardar');
        setAdjUrlError(res.message || 'Error al guardar');
      }
    } catch {
      message.error('Error de red');
      setAdjUrlError('Error de red');
    }
  };

  const handleUploadAdjunto = async (file) => {
    setUploadingAdj(true);
    setAdjUrlError('');
    try {
      if (!isEdit) {
        // Upload to get a URL, then buffer it
        const res = await api.admin.catalogo.uploadProductoImagen(file);
        if (res.status === 'ok') {
          setAdjBorradores(prev => [...prev, { url: res.url, tempId: Date.now() }]);
        } else {
          setAdjUrlError(res.error || 'Error al subir');
        }
      } else {
        const res = await api.admin.catalogo.uploadProductoAdjunto(id, file);
        if (res.status === 'success') {
          setAdjuntos(prev => [...prev, res.data]);
          message.success('Imagen subida');
        } else {
          message.error(res.message || 'Error al subir');
          setAdjUrlError(res.message || 'Error al subir');
        }
      }
    } catch {
      setAdjUrlError('Error de red al subir la imagen');
    } finally {
      setUploadingAdj(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // La IA solo propone: el texto queda en el formulario y se guarda con el
  // producto. Si ya había una descripción, se pregunta antes de pisarla.
  const [generandoDesc, setGenerandoDesc] = useState(false);
  const [modoPrecio, setModoPrecio] = useState('manual');
  const generarDescripcion = () => {
    const pedir = async () => {
      setGenerandoDesc(true);
      try {
        const res = await api.admin.catalogo.descripcionIA(form.nombre, catSeleccionadas);
        if (res.status === 'success') {
          setForm(f => ({ ...f, descripcion: res.data.descripcion }));
          message.success('Descripción generada. Revisala antes de guardar.');
        } else {
          message.error(res.message || 'No se pudo generar la descripción');
        }
      } catch {
        message.error('Error de red');
      } finally {
        setGenerandoDesc(false);
      }
    };
    if (!form.descripcion?.trim()) return pedir();
    Modal.confirm({
      title: '¿Reemplazar la descripción?',
      content: 'La descripción que está cargada se va a reemplazar por la que genere la IA.',
      okText: 'Reemplazar', cancelText: 'Cancelar', onOk: pedir,
    });
  };

  const handleSerpapiSearch = async () => {
    const q = serpapiQuery.trim();
    if (!q) return;
    setSerpapiLoading(true);
    setSerpapiError('');
    setSerpapiResults(null);
    try {
      const res = await api.admin.catalogo.serpapiSearch(q);
      if (res.status === 'success') {
        setSerpapiResults(res.data || []);
      } else {
        setSerpapiError(res.message || 'Error al buscar imágenes');
      }
    } catch {
      setSerpapiError('Error de red');
    } finally {
      setSerpapiLoading(false);
    }
  };

  const handleAddSerpapiImage = async (originalUrl) => {
    setAddingSerpapiUrl(originalUrl);
    setAdjUrlError('');
    try {
      if (!isEdit) {
        setAdjBorradores(prev => [...prev, { url: originalUrl, tempId: Date.now() }]);
      } else {
        const res = await api.admin.catalogo.addProductoAdjunto(id, originalUrl);
        if (res.status === 'success') {
          setAdjuntos(prev => [...prev, res.data]);
        } else {
          setAdjUrlError(res.message || 'Error al guardar imagen');
        }
      }
    } catch {
      setAdjUrlError('Error de red al guardar imagen');
    } finally {
      setAddingSerpapiUrl('');
    }
  };

  const handleDeleteAdjunto = async (adjunto) => {
    if (!isEdit) {
      setAdjBorradores(prev => prev.filter(a => a.tempId !== adjunto.tempId));
      return;
    }
    setDeletingAdjId(adjunto.id);
    try {
      await api.admin.catalogo.deleteProductoAdjunto(adjunto.id);
      setAdjuntos(prev => prev.filter(a => a.id !== adjunto.id));
    } finally {
      setDeletingAdjId(null);
    }
  };

  // ── Precios handlers ─────────────────────────────────────────────────────────

  const openEditPrecio = (p) => {
    setInlineEditingTipo(p.id_tipo_precio);
    setInlineListaId(String(p.id_tipo_precio));
    setInlinePrecioVal(String(p.precio));
    setPrecioInlineMsg({ type: '', text: '' });
    setShowPrecioForm(true);
  };

  const openAddPrecio = () => {
    setInlineEditingTipo(null);
    setInlineListaId('');
    setInlinePrecioVal('');
    setPrecioInlineMsg({ type: '', text: '' });
    setShowPrecioForm(true);
  };

  const closePrecioForm = () => {
    setShowPrecioForm(false);
    setInlineEditingTipo(null);
    setInlineListaId('');
    setInlinePrecioVal('');
    setPrecioInlineMsg({ type: '', text: '' });
  };

  const handleGuardarPrecioInline = async () => {
    if (!inlineListaId || inlinePrecioVal === '') return;
    const tipo = tiposPrecio.find(t => String(t.id) === inlineListaId);
    if (!isEdit) {
      setPreciosBorradores(prev => {
        const entry = { id_tipo_precio: Number(inlineListaId), tipo_nombre: tipo?.nombre || '', precio: Number(inlinePrecioVal) };
        const exists = prev.find(p => String(p.id_tipo_precio) === inlineListaId);
        return exists ? prev.map(p => String(p.id_tipo_precio) === inlineListaId ? entry : p) : [...prev, entry];
      });
      closePrecioForm();
      return;
    }
    setSavingPrecioInline(true);
    setPrecioInlineMsg({ type: '', text: '' });
    try {
      const res = await api.admin.catalogo.agregarPrecio(id, {
        id_tipo_precio: Number(inlineListaId),
        precio: Number(inlinePrecioVal),
      });
      if (res.status === 'success') {
        const resP = await api.admin.catalogo.getProducto(id);
        if (resP.status === 'success') setPrecios(resP.data.precios || []);
        message.success('Precio guardado');
        closePrecioForm();
      } else {
        message.error(res.message || 'Error al guardar precio');
        setPrecioInlineMsg({ type: 'error', text: res.message || 'Error al guardar precio' });
      }
    } catch {
      message.error('Error de red o servidor');
      setPrecioInlineMsg({ type: 'error', text: 'Error de red o servidor' });
    } finally {
      setSavingPrecioInline(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }

  // El costo no es un precio de venta: con precios automáticos se carga en su
  // propio campo, y sin ellos no se usa. La lista de abajo es solo de venta.
  const preciosAuto = tieneCapacidad('precios_automaticos');
  const tiposVenta = tiposPrecio.filter(t => t.tipo !== 'costo');
  const idsVenta = new Set(tiposVenta.map(t => t.id));
  const preciosActuales = (isEdit ? precios : preciosBorradores).filter(p => idsVenta.has(p.id_tipo_precio));
  // En automático el precio lo calcula el backend: no se edita a mano.
  const precioCalculado = preciosAuto && isEdit && modoPrecio === 'automatico';
  const tiposUsados = new Set(preciosActuales.map(p => p.id_tipo_precio));
  const tiposDisponibles = tiposVenta.filter(t => !tiposUsados.has(t.id));
  const hayTiposSinPrecio = tiposDisponibles.length > 0;

  const adjActuales = isEdit ? adjuntos : adjBorradores;

  const ActionBar = () => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <Button onClick={() => navigate(clientPath('/admin/productos'))}>Cancelar</Button>
      <Button
        type="primary" htmlType="submit" form="producto-form" loading={saving}
        style={{ background: primary, borderColor: primary, color: primaryText }}
      >
        {saving ? 'Guardando...' : isEdit ? 'Guardar producto' : 'Crear producto'}
      </Button>
    </div>
  );

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
          <Button
            type="text" icon={<ArrowLeftOutlined />}
            onClick={() => navigate(clientPath('/admin/productos'))}
            style={{ padding: '0 4px', flexShrink: 0 }}
          />
          <div style={{ minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
            title={isEdit ? (producto?.nombre || 'Editar producto') : 'Nuevo producto'}>
            <Text strong style={{ fontSize: 22 }}>
              {isEdit ? (producto?.nombre || 'Editar producto') : 'Nuevo producto'}
            </Text>
          </div>
          {isEdit && producto?.origen === 'erp' && <Tag color="blue" style={{ flexShrink: 0 }}>Sincronizado ERP</Tag>}
        </div>
        <ActionBar />
      </div>

      {isEdit && producto?.origen === 'erp' && (
        <Alert
          type="warning"
          message="Este producto se sincroniza desde el ERP. Los cambios manuales pueden ser sobreescritos en la próxima sincronización."
          style={{ marginBottom: 16 }}
        />
      )}
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <form id="producto-form" onSubmit={handleSubmit}>

        {/* Nombre y descripción */}
        <Section title="Nombre y descripción">
          <Field label="Nombre" style={{ marginBottom: 16 }}>
            <Input value={form.nombre} onChange={set('nombre')} required placeholder="Ej: Camisa de lino" size="large" />
          </Field>
          <Field label="Descripción">
            {tieneCapacidad('descripciones_ia') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <Button size="small" icon={<ThunderboltOutlined />} loading={generandoDesc} onClick={generarDescripcion}
                  disabled={!form.nombre.trim()}>
                  Generar con IA
                </Button>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Usa el nombre y las categorías. Revisá el texto antes de guardar.
                </Text>
              </div>
            )}
            <Input.TextArea
              value={form.descripcion} onChange={set('descripcion')}
              placeholder="Describí el producto para tus clientes..."
              autoSize={{ minRows: 4 }} style={{ fontSize: 14 }}
            />
          </Field>
        </Section>

        {/* Imágenes */}
        <Section title="Imágenes">
          {/* Thumbnail grid */}
          {adjActuales.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
              {adjActuales.map((adj, i) => (
                <div key={adj.id ?? adj.tempId} style={{ position: 'relative' }}>
                  <img
                    src={resolveAdjunto(adj)}
                    alt={`imagen ${i + 1}`}
                    style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', display: 'block' }}
                    onError={e => { e.target.style.opacity = '0.3'; }}
                  />
                  <button
                    type="button"
                    disabled={deletingAdjId === adj.id}
                    onClick={() => handleDeleteAdjunto(adj)}
                    style={{
                      position: 'absolute', top: 4, right: 4,
                      width: 20, height: 20, borderRadius: '50%',
                      background: 'rgba(0,0,0,0.55)', border: 'none',
                      color: '#fff', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, padding: 0,
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add by URL */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
            <Input
              value={adjUrl}
              onChange={e => { setAdjUrl(e.target.value); setAdjUrlError(''); }}
              placeholder="https://... (URL de imagen)"
              prefix={<LinkOutlined style={{ color: '#9ca3af' }} />}
              onPressEnter={handleAddAdjuntoUrl}
              style={{ flex: 1 }}
            />
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddAdjuntoUrl}
              disabled={!adjUrl.trim()}
            >
              Agregar
            </Button>
          </div>

          {/* Upload file */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) handleUploadAdjunto(file);
              }}
            />
            <Button
              icon={<UploadOutlined />}
              loading={uploadingAdj}
              onClick={() => fileInputRef.current?.click()}
              style={{ borderStyle: 'dashed' }}
            >
              {uploadingAdj ? 'Subiendo...' : 'Subir archivo (JPG, PNG, WebP)'}
            </Button>
          </div>

          {adjUrlError && (
            <Text style={{ display: 'block', marginTop: 8, fontSize: 13, color: '#dc2626' }}>
              {adjUrlError}
            </Text>
          )}

          {/* SerpAPI image search: solo con la capacidad prendida */}
          {tieneCapacidad('imagenes_serpapi') && (<>
          <Divider style={{ margin: '16px 0 12px' }} />
          <Text style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 8 }}>
            Buscar imágenes
          </Text>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <Input
              value={serpapiQuery}
              onChange={e => setSerpapiQuery(e.target.value)}
              onPressEnter={handleSerpapiSearch}
              placeholder="Ej: camisa de lino blanca"
              prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
              style={{ flex: 1 }}
            />
            <Button
              icon={<SearchOutlined />}
              loading={serpapiLoading}
              disabled={!serpapiQuery.trim()}
              onClick={handleSerpapiSearch}
            >
              Buscar
            </Button>
          </div>
          {serpapiError && (
            <Text style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#dc2626' }}>
              {serpapiError}
            </Text>
          )}
          {serpapiResults !== null && serpapiResults.length === 0 && (
            <Text type="secondary" style={{ fontSize: 13 }}>Sin resultados para esa búsqueda.</Text>
          )}
          {serpapiResults && serpapiResults.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {serpapiResults.map((img, i) => {
                const alreadyAdded = adjActuales.some(a => (a.url || a.adjunto) === img.original);
                const isAdding = addingSerpapiUrl === img.original;
                return (
                  <div
                    key={i}
                    title={img.title}
                    style={{ position: 'relative', cursor: alreadyAdded ? 'default' : 'pointer' }}
                    onClick={() => !alreadyAdded && !isAdding && handleAddSerpapiImage(img.original)}
                  >
                    <img
                      src={img.thumbnail}
                      alt={img.title}
                      style={{
                        width: 80, height: 80, objectFit: 'cover', borderRadius: 6,
                        border: alreadyAdded ? '2px solid #16a34a' : '2px solid transparent',
                        opacity: isAdding ? 0.5 : 1,
                        transition: 'border-color 0.15s',
                      }}
                      onError={e => { e.target.style.opacity = '0.2'; }}
                    />
                    {alreadyAdded && (
                      <div style={{
                        position: 'absolute', inset: 0, borderRadius: 6,
                        background: 'rgba(22,163,74,0.18)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <span style={{ color: '#16a34a', fontSize: 18, fontWeight: 700 }}>✓</span>
                      </div>
                    )}
                    {isAdding && (
                      <div style={{
                        position: 'absolute', inset: 0, borderRadius: 6,
                        background: 'rgba(255,255,255,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Spin size="small" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </>)}
        </Section>

        {/* Precios */}
        <Section title="Precios">
          {preciosAuto && isEdit && (
            <PrecioAutomatico
              productoId={id} primary={primary} primaryText={primaryText}
              onModo={setModoPrecio}
              onGuardado={async () => {
                const resP = await api.admin.catalogo.getProducto(id);
                if (resP.status === 'success') setPrecios(resP.data.precios || []);
              }}
            />
          )}
          {preciosAuto && !isEdit && (
            <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 12 }}>
              El precio automático (costo y margen) se configura después de crear el producto.
            </Text>
          )}
          {productoTieneVariantes && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Precio base del producto"
              description="Este precio se aplica cuando una variante no tiene un precio propio definido. Si necesitás que una variante tenga un valor distinto, podés cargarlo directamente en la tabla de variantes."
            />
          )}
          {preciosActuales.length > 0 && (
            <div style={{ marginBottom: showPrecioForm ? 16 : (hayTiposSinPrecio ? 16 : 0) }}>
              {preciosActuales.map(p => (
                <div key={p.id_tipo_precio} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 0', borderBottom: '1px solid #f3f4f6',
                }}>
                  <Text style={{ fontSize: 14, color: '#374151' }}>{p.tipo_nombre}</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Text strong style={{ fontSize: 14 }}>$ {Number(p.precio).toFixed(2)}</Text>
                    {!precioCalculado && <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditPrecio(p)} />}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showPrecioForm && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Field label="Lista de precios" style={{ flex: '1 1 160px' }}>
                  <Select
                    style={{ width: '100%' }}
                    value={inlineListaId || undefined}
                    onChange={v => setInlineListaId(v)}
                    placeholder="Seleccionar..."
                    disabled={!!inlineEditingTipo}
                    options={
                      inlineEditingTipo
                        ? tiposPrecio.filter(t => t.id === inlineEditingTipo).map(t => ({ value: String(t.id), label: t.nombre }))
                        : tiposDisponibles.map(t => ({ value: String(t.id), label: t.nombre }))
                    }
                  />
                </Field>
                <Field label="Precio" style={{ flex: '1 1 120px' }}>
                  <Input
                    type="number" min={0} step="0.01" prefix="$"
                    value={inlinePrecioVal} onChange={e => setInlinePrecioVal(e.target.value)}
                    placeholder="0.00" autoFocus
                  />
                </Field>
                <div style={{ display: 'flex', gap: 6, marginBottom: 1 }}>
                  <Button
                    type="primary" loading={savingPrecioInline}
                    disabled={!inlineListaId || inlinePrecioVal === ''}
                    onClick={handleGuardarPrecioInline}
                    style={{ background: primary, borderColor: primary, color: primaryText }}
                  >
                    Guardar
                  </Button>
                  <Button icon={<CloseOutlined />} onClick={closePrecioForm} />
                </div>
              </div>
              {precioInlineMsg.text && (
                <Text style={{ display: 'block', marginTop: 8, fontSize: 13, color: precioInlineMsg.type === 'success' ? '#16a34a' : '#dc2626' }}>
                  {precioInlineMsg.text}
                </Text>
              )}
            </div>
          )}

          {!showPrecioForm && hayTiposSinPrecio && !precioCalculado && (
            <Button icon={<PlusOutlined />} onClick={openAddPrecio} style={{ borderStyle: 'dashed' }}>
              Agregar precio
            </Button>
          )}
        </Section>

        {/* Inventario — se oculta cuando el producto maneja variantes */}
        {!productoTieneVariantes && (
          <Section title="Inventario">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <Field label="Stock" helper="Cantidad disponible actual">
                <Input type="number" min={0} value={form.stock} onChange={set('stock')} />
              </Field>
              <Field label="Stock mínimo" helper="Alerta cuando baje de este valor">
                <Input type="number" min={0} value={form.stock_minimo} onChange={set('stock_minimo')} />
              </Field>
              <Field label="Unidad" helper="Ej: kg, un, lt">
                <Input value={form.unidad} onChange={set('unidad')} placeholder="un" />
              </Field>
            </div>
          </Section>
        )}

        {/* Variantes — solo en modo edición */}
        {isEdit && (
          <Section
            title="Variantes"
            description="Definí propiedades como color o talle para generar variantes con stock y precio por combinación."
          >
            <VariantesSection productoId={id} primary={primary} primaryText={primaryText} onVariantesChange={setProductoTieneVariantes} tiposPrecio={preciosAuto ? tiposPrecio : tiposVenta} preciosProducto={preciosActuales} />
          </Section>
        )}

        {/* Código */}
        <Section title="Código">
          <Field label="Código (SKU)" helper="Código interno para identificar el producto. Debe ser único.">
            <Input value={form.codigo} onChange={set('codigo')} placeholder="Ej: CAM-LIN-001" style={{ maxWidth: 280 }} />
          </Field>
        </Section>

        {/* Categorías */}
        {categorias.length > 0 && (
          <Section title="Categorías" description="Asigná el producto a una o más categorías para que tus clientes puedan encontrarlo más fácil.">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {buildCatTree(categorias).map(cat => (
                <div key={cat.id} style={{ paddingLeft: cat.depth * 20 }}>
                  <Checkbox checked={catSeleccionadas.includes(cat.id)} onChange={() => toggleCat(cat.id)}>
                    <Text style={{ fontWeight: cat.depth === 0 ? 600 : 400, fontSize: 13 }}>{cat.nombre}</Text>
                  </Checkbox>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Productos asociados */}
        <Section title="Productos asociados" description="Asociá productos relacionados que se mostrarán como sugerencias en el detalle del producto.">
          {/* Lista de asociados actuales */}
          {asociados.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {asociados.map(a => (
                <div key={a.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff',
                }}>
                  {a.imagen && (
                    <img src={resolveImg(a.imagen)} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, background: '#f5f5f5' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 13, fontWeight: 500, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.nombre}
                    </Text>
                    {a.codigo && <Text type="secondary" style={{ fontSize: 11 }}>CÓD. {a.codigo}</Text>}
                  </div>
                  <Button type="text" size="small" danger icon={<CloseOutlined />}
                    onClick={() => setAsociados(prev => prev.filter(x => x.id !== a.id))} />
                </div>
              ))}
            </div>
          )}

          {/* Buscador */}
          <Input.Search
            placeholder="Buscar producto por nombre o código..."
            value={asociadoQuery}
            onChange={e => setAsociadoQuery(e.target.value)}
            loading={buscandoAsociado}
            onSearch={async (val) => {
              const q = val.trim();
              if (!q) { setAsociadoResults([]); return; }
              setBuscandoAsociado(true);
              try {
                const res = await api.admin.catalogo.getProductos({ q, limit: 10 });
                if (res.status === 'success') {
                  const items = (res.data?.items || []).filter(p => p.id !== Number(id) && !asociados.find(a => a.id === p.id));
                  setAsociadoResults(items);
                }
              } catch {}
              setBuscandoAsociado(false);
            }}
            allowClear
            onClear={() => setAsociadoResults([])}
          />

          {/* Resultados */}
          {asociadoResults.length > 0 && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
              {asociadoResults.map(p => (
                <div key={p.id}
                  onClick={() => {
                    setAsociados(prev => [...prev, { id: p.id, nombre: p.nombre, codigo: p.codigo, imagen: p.imagen }]);
                    setAsociadoResults(prev => prev.filter(x => x.id !== p.id));
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  {p.imagen && (
                    <img src={resolveImg(p.imagen)} alt="" style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 4, background: '#f5f5f5' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 13, fontWeight: 500 }}>{p.nombre}</Text>
                    {p.codigo && <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>CÓD. {p.codigo}</Text>}
                  </div>
                  <PlusOutlined style={{ color: '#9ca3af' }} />
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Visibilidad */}
        <Section title="Visibilidad">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text style={{ fontWeight: 500, fontSize: 14 }}>Mostrar en el catálogo</Text>
                <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
                  El producto aparece en la tienda y puede ser comprado por los clientes.
                </Text>
              </div>
              <Switch checked={form.visible} onChange={(v) => setForm(f => ({ ...f, visible: v }))} />
            </div>
            <Divider style={{ margin: 0 }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text style={{ fontWeight: 500, fontSize: 14 }}>Producto destacado</Text>
                <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
                  Aparece en la sección "Productos destacados" de la página de inicio.
                </Text>
              </div>
              <Switch checked={form.destacado} onChange={(v) => setForm(f => ({ ...f, destacado: v }))} />
            </div>
            {isEdit && (
              <>
                <Divider style={{ margin: 0 }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Text style={{ fontWeight: 500, fontSize: 14, color: '#dc2626' }}>Eliminar producto</Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
                      El producto se da de baja y deja de aparecer en todos los listados.
                    </Text>
                  </div>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => Modal.confirm({
                      title: '¿Eliminar producto?',
                      content: 'El producto se dará de baja y no aparecerá en ningún listado. Esta acción no se puede deshacer.',
                      okText: 'Eliminar',
                      okButtonProps: { danger: true },
                      cancelText: 'Cancelar',
                      onOk: async () => {
                        await api.admin.catalogo.eliminarProducto(id);
                        message.success('Producto eliminado');
                        navigate(clientPath('/admin/productos'));
                      },
                    })}
                  >
                    Eliminar
                  </Button>
                </div>
              </>
            )}
          </div>
        </Section>

        {/* Action bar bottom */}
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <Button onClick={() => navigate(clientPath('/admin/productos'))}>Cancelar</Button>
          <Button
            type="primary" htmlType="submit" loading={saving}
            style={{ background: primary, borderColor: primary, color: primaryText }}
          >
            {saving ? 'Guardando...' : isEdit ? 'Guardar producto' : 'Crear producto'}
          </Button>
        </div>
      </form>
    </div>
  );
};
